// v4 (V4-H2, spec-v4 2.3, 3.1, 5.1): where a video lives, on its own screen. The breadcrumb (Channel › Collection ›
// Video), the "The series has new theme music. Use it?" notices (never folded), a "From the series" fold with each
// shared value and its chip, and the two moves in the ••• menu: "Move to…" and "Make this a series" (promote).
import { useState } from "react";
import {
  collections,
  collectionsChanged,
  crumbs,
  fitsGroup,
  kindNoun,
  moveTargets,
  promoteLabel,
  settingRows,
  toastError,
  useChannelCollections,
  type CollectionCard,
  type VideoSettings,
} from "../collections";
import { director } from "../director";
import { formatShort } from "../formats";
import { navigate } from "../router";
import { toast, useRefresh, useStore } from "../store";
import type { Project } from "../types";
import { AdoptButton } from "../screens/Collection";
import { Icon, Sheet, useLoad } from "./bits";
import { Crumbs } from "./Crumbs";
import { Fold } from "./Fold";
import type { MoreItem } from "./MoreMenu";
import { Notice } from "./Notice";

export type VideoPlace = { settings: VideoSettings | null; collection: CollectionCard | null; siblings: CollectionCard[] | null };

/** The video's settings (GET /projects/:id/settings: its collection, chips, pending) and its channel's collections. */
export function useVideoPlace(p: Pick<Project, "id" | "channelId">): VideoPlace {
  const tick = useRefresh("collections", "project");
  const { data: settings } = useLoad<VideoSettings | null>(() => collections.videoSettings(p.id).catch(() => null), [p.id, tick]);
  const siblings = useChannelCollections(p.channelId);
  const collection = settings?.collectionId ? (siblings?.find((c) => c.id === settings.collectionId) ?? null) : null;
  return { settings: settings ?? null, collection, siblings };
}

export function VideoCrumbs({ p, place }: { p: Pick<Project, "title" | "channelId">; place: VideoPlace }) {
  const channel = useStore((s) => s.me?.channels.find((c) => c.id === p.channelId) ?? null);
  return <Crumbs items={crumbs({ channel: channel ?? { id: p.channelId ?? "", name: "Your videos" }, collection: place.collection, video: { title: p.title } })} />;
}

/** Pending render-time changes from the collection: one notice each, with Use it. */
export function SeriesNotices({ p, place }: { p: Pick<Project, "id" | "title">; place: VideoPlace }) {
  const pending = place.settings?.pending ?? [];
  if (!pending.length) return null;
  return (
    <div className="series-notices">
      {pending.map((item) => (
        <Notice key={item.key} tone="info" code="SETTINGS_CHANGED" text={item.text} action={<AdoptButton projectId={p.id} item={item} title={p.title} />} />
      ))}
    </div>
  );
}

/** "From the series": every shared value this video uses and where it came from. Only for a video in a collection. */
export function SeriesFold({ place }: { place: VideoPlace }) {
  const s = place.settings;
  if (!s?.collectionId) return null;
  const rows = settingRows(s.settings, s.kind).filter((r) => r.from !== "default");
  const inherited = rows.filter((r) => r.from === "collection" || r.from === "channel").length;
  const noun = kindNoun(s.kind);
  return (
    <Fold id="video.series" title={`From the ${noun}`} count={inherited} summary={place.collection?.name ?? ""} className="series-fold">
      <div className="krows">
        {rows.map((r) => (
          <div className="krow" key={r.key}>
            <span className="krow-label">{r.label}</span>
            <span className="krow-value">{r.text}</span>
            <span className={`from-chip from-${r.from}`}>{r.chip}</span>
          </div>
        ))}
        {!rows.length && <p className="muted">This video sets everything itself.</p>}
      </div>
    </Fold>
  );
}

/** The ••• menu items for where the video lives. */
export function placeMenuItems(p: Pick<Project, "format">, place: VideoPlace, open: (what: "move" | "promote") => void): MoreItem[] {
  const label = promoteLabel(p.format);
  if (!label) return [];
  const inOne = !!place.settings?.collectionId;
  const fits = (place.siblings ?? []).filter((c) => fitsGroup(p.format, c.group) && c.id !== place.settings?.collectionId);
  return [
    { id: "move", label: "Move to…", hint: inOne ? `Now in ${place.collection?.name ?? "a collection"}` : fits.length ? `${fits.length} collection${fits.length === 1 ? "" : "s"} can take it` : "Into a collection, or back on its own", icon: "stack", disabled: !place.settings, onSelect: () => open("move") },
    ...(inOne ? [] : [{ id: "promote", label, hint: "This video becomes the first episode", icon: "sparkle", disabled: !place.settings, onSelect: () => open("promote") }]),
  ];
}

/** "Move to…": the channel's collections that take this format, or back to a one-off. */
export function MoveSheet({ p, place, onClose }: { p: Pick<Project, "id" | "title" | "format">; place: VideoPlace; onClose: () => void }) {
  const current = place.settings?.collectionId ?? null;
  const { fits, others } = moveTargets(p.format, place.siblings ?? [], current);
  const [busy, setBusy] = useState<string | null>(null);
  const move = async (to: CollectionCard | null) => {
    setBusy(to?.id ?? "none");
    try {
      await collections.move(p.id, to?.id ?? null);
      collectionsChanged();
      toast(to ? `Moved to ${to.name}. It uses what the ${kindNoun(to.kind)} shares now.` : "It's a one-off again.", "good");
      director.context(`The user moved "${p.title}" (${p.id}) ${to ? `into the collection "${to.name}" (${to.id})` : "out of its collection; it's a one-off now"} by tapping.`);
      onClose();
    } catch (e) {
      toastError("Couldn't move it.", e);
      setBusy(null);
    }
  };
  return (
    <Sheet title="Move to…" onClose={onClose} className="move-sheet">
      <div className="sheet-body">
        <ul className="move-list">
          {fits.map((c) => (
            <li key={c.id}>
              <button data-action="video.move" type="button" className="move-item" disabled={!!busy} onClick={() => void move(c)}>
                <span className="move-art">{c.thumbUrl ?? c.coverUrl ? <img src={(c.thumbUrl ?? c.coverUrl)!} alt="" /> : <Icon name="stack" size={20} />}</span>
                <span className="move-text">
                  <span className="move-name">{c.name}</span>
                  <span className="move-meta">
                    {c.videoCount} video{c.videoCount === 1 ? "" : "s"}
                  </span>
                </span>
                {busy === c.id ? <span className="muted">Moving</span> : <Icon name="chevron" size={16} />}
              </button>
            </li>
          ))}
          {current && (
            <li>
              <button data-action="video.move" type="button" className="move-item" disabled={!!busy} onClick={() => void move(null)}>
                <span className="move-art">
                  <Icon name="film" size={20} />
                </span>
                <span className="move-text">
                  <span className="move-name">On its own</span>
                  <span className="move-meta">A one-off on the channel</span>
                </span>
                {busy === "none" ? <span className="muted">Moving</span> : <Icon name="chevron" size={16} />}
              </button>
            </li>
          )}
        </ul>
        {!fits.length && <p className="muted">{current ? `No other collection on this channel takes ${formatShort(p.format).toLowerCase()}s.` : `No collection on this channel takes ${formatShort(p.format).toLowerCase()}s yet. Make this one a series instead.`}</p>}
        {!!others.length && <p className="sheet-note">{others.map((c) => c.name).join(", ")} take other kinds of video.</p>}
        <p className="sheet-note">Finished versions stay as they are. Shots made after the move use what the collection shares.</p>
      </div>
    </Sheet>
  );
}

/** "Make this a series": a new collection of the right kind with this video as its first; its cast moves up. */
export function PromoteSheet({ p, onClose }: { p: Pick<Project, "id" | "title" | "format" | "brief">; onClose: () => void }) {
  const label = promoteLabel(p.format) ?? "Make this a collection";
  const noun = label.replace(/^Make this an? /, "");
  const [name, setName] = useState(p.title);
  const [busy, setBusy] = useState(false);
  const cast = Array.isArray((p.brief as { characters?: unknown[] }).characters) ? ((p.brief as { characters?: { name: string }[] }).characters ?? []) : [];
  return (
    <Sheet title={label} onClose={onClose} className="promote-sheet">
      <form
        className="sheet-body"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim()) return;
          setBusy(true);
          try {
            const c = await collections.promote(p.id, name.trim());
            collectionsChanged();
            toast(`${c.name} is a ${noun} now, with "${p.title}" as its first video.`, "good");
            director.context(`The user made "${p.title}" (${p.id}) the first video of a new ${noun} "${c.name}" (${c.id}) by tapping.`);
            onClose();
            navigate(`/k/${encodeURIComponent(c.id)}`);
          } catch (err) {
            toastError(`Couldn't make the ${noun}.`, err);
            setBusy(false);
          }
        }}
      >
        <p>
          “{p.title}” becomes the first video.{" "}
          {cast.length ? `${cast.map((c) => c.name).join(", ")} join${cast.length === 1 ? "s" : ""} the ${noun}'s cast, so the next videos draw ${cast.length === 1 ? "them" : "them all"} the same way.` : `The next ones you make in it start with its look and shape.`}
        </p>
        <label className="field">
          <span className="field-label">Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} data-autofocus />
        </label>
        <button data-action="video.make-series" type="submit" className="btn btn-primary btn-block" disabled={busy || !name.trim()}>
          {busy ? "Making it" : `Make the ${noun}`}
        </button>
        <p className="sheet-note">Free. Nothing is remade.</p>
      </form>
    </Sheet>
  );
}
